import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authAPI } from "../services/authAPI";
import { validatePassword, formatAPIError } from "../utils/validation";
import PasswordInput from "../components/common/PasswordInput";
import Header from "../components/layout/Header";
import "./styles/ChangePassword.css";

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    setSuccess("");
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.oldPassword) {
      validationErrors.oldPassword = "Current password is required";
    }

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      validationErrors.newPassword = passwordError;
    }

    if (!formData.confirmPassword) {
      validationErrors.confirmPassword = "Please confirm your new password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      validationErrors.confirmPassword = "Passwords do not match";
    }

    if (
      formData.newPassword &&
      formData.oldPassword &&
      formData.newPassword === formData.oldPassword
    ) {
      validationErrors.newPassword = "New password must be different from current password";
    }

    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccess("");

    try {
      const response = await authAPI.changePassword({
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
      });

      setSuccess(response.data.message || "Password changed successfully!");

      setFormData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setTimeout(() => {
        navigate("/dashboard", { state: { message: "Password changed successfully!" } });
      }, 2000);
    } catch (error) {
      const errorMessage = formatAPIError(error);

      if (errorMessage.includes("Invalid old password")) {
        setErrors({ oldPassword: "Current password is incorrect" });
      } else if (errorMessage.includes("Password must be 8+ characters")) {
        setErrors({
          newPassword: "Password must be 8+ characters, include uppercase, number, and special symbol",
        });
      } else if (errorMessage.includes("Cannot reuse last 3 passwords")) {
        setErrors({
          newPassword: "This password was recently used. Please choose a different password.",
        });
      } else if (errorMessage.includes("User not found")) {
        setErrors({ submit: "User session expired. Please login again." });
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <Header />

      <div className="cp-page">
        <div className="cp-card">
          <div className="cp-top">
            <div className="cp-icon">
              {/* inline svg lock to avoid adding deps */}
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M7 10V8a5 5 0 0 1 10 0v2"
                  stroke="#2563EB"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M7 10h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"
                  stroke="#2563EB"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h1 className="cp-title">Change Password</h1>
            <p className="cp-subtitle">Update your account password</p>
          </div>

          <div className="cp-body">
            {errors.submit && <div className="cp-alert cp-alert--error">{errors.submit}</div>}
            {success && <div className="cp-alert cp-alert--success">{success}</div>}

            <form onSubmit={handleSubmit}>
              <div className="cp-field">
                <label className="cp-label">Current Password</label>
                <PasswordInput
                  name="oldPassword"
                  value={formData.oldPassword}
                  onChange={handleChange}
                  placeholder="Enter current password"
                  disabled={loading}
                  className={errors.oldPassword ? "cp-input cp-input--error" : "cp-input"}
                />
                {errors.oldPassword && <div className="cp-error">{errors.oldPassword}</div>}
              </div>

              <div className="cp-field">
                <label className="cp-label">New Password</label>
                <PasswordInput
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  disabled={loading}
                  className={errors.newPassword ? "cp-input cp-input--error" : "cp-input"}
                />
                {errors.newPassword && <div className="cp-error">{errors.newPassword}</div>}
              </div>

              <div className="cp-field">
                <label className="cp-label">Confirm New Password</label>
                <PasswordInput
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                  disabled={loading}
                  className={errors.confirmPassword ? "cp-input cp-input--error" : "cp-input"}
                />
                {errors.confirmPassword && <div className="cp-error">{errors.confirmPassword}</div>}
              </div>

              <div className="cp-req">
                <div className="cp-req-title">Password Requirements:</div>
                <ul className="cp-req-list">
                  <li>At least 8 characters long</li>
                  <li>Contains uppercase and lowercase letters</li>
                  <li>Contains at least one number</li>
                  <li>Different from current password</li>
                </ul>
              </div>

              <div className="cp-actions">
                <button
                  type="button"
                  className="cp-btn cp-btn--ghost"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </button>

                <button type="submit" className="cp-btn cp-btn--primary" disabled={loading}>
                  {loading ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangePassword;
